import React, { useContext } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"
import Swal from "sweetalert2"

// Estilos base estandarizados
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const CrearSolicitudIngEgrIndex = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Mutación para eliminar el registro (Ahora puede recibir tsolcodigo dentro de data)
  const { mutateAsync: SaveEliminacionSolicitud, isPending: isDeletingSolicitud } = useMutation({
    queryKey: ["isDeletingSolicitud"],
    fn: async (data) => {
      const response = await api.post("/CrearSolicitudIngEgr/eliminarSolicitud", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["CrearSolicitudIngEgr"] })
    },
  })

  // Función para manejar el modal de confirmación antes de eliminar
  const handleDelete = async (row) => {
    const confirmacion = await Swal.fire({
      title: "¿Eliminar Solicitud?",
      html: `<p>¿Está seguro que desea eliminar la Solicitud Nro: <strong>${row.original.solnumero}</strong>?</p>
             <p style="font-size: 0.9em; color: #666;">Esta acción no se puede deshacer.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#196C87",
      reverseButtons: true,
    })

    if (confirmacion.isConfirmed) {
      try {
        await SaveEliminacionSolicitud({
          solnumero: row.original.solnumero,
          loccodigo: row.original.loccodigo,
          tsolcodigo: row.original.tsolcodigo, // NUEVO: Se envía el código para mayor seguridad
        })
      } catch (err) {
        console.error("Error eliminando la Solicitud:", err)
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Solicitudes de Inventario</b>
        </div>

        <CustomBackdrop isLoading={isDeletingSolicitud} />

        <Box className={StyledRoot}>
          {/* GRILLA PRINCIPAL */}
          <CustomConditionalActionsTableServer
            endpoint="/CrearSolicitudIngEgr/getAllSolicitudes"
            errorMsgFilterSearch="Error al cargar datos de las Solicitudes"
            queryKeyModal="CrearSolicitudIngEgr"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )

              const actions = []

              if (editarAction) {
                actions.push({
                  label: "Editar",
                  key: "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  // row.original ahora contiene tsolcodigo para que la pantalla de Editar lo reciba
                  onClick: (row) => navigate("editar", { state: row.original }),
                })
              }

              if (eliminarAction) {
                // Validación visual rápida: solo mostramos el botón eliminar si está PENDIENTE
                if (row.original.sgastatus === "PENDIENTE") {
                  actions.push({
                    label: "Eliminar",
                    key: "ELIMINAR",
                    icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                    onClick: (row) => handleDelete(row), // Llama al Modal de Confirmación
                  })
                }
              }

              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )

              const toolbarActions = []
              if (crearAction) {
                toolbarActions.push({
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction?.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        const title = "Reporte de Solicitudes de Inventario"
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            title,
                            `${title} ${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          title,
                          `${title} ${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) =>
                        handleAllExportDataCSV(data, `Solicitudes_Inv_${new Date().toLocaleString()}`),
                    },
                  ],
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "loccodigo",
                header: "Loc.",
                size: 80,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "solnumero",
                header: "Nº Solicitud",
                size: 160,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tsolcodigo",
                header: "Cód. Tipo",
                size: 100,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tsolInEg",
                header: "Naturaleza",
                size: 160,
                Cell: ({ cell }) => {
                  const valor = cell.getValue()
                  // Mapeo estandarizado a mayúsculas para coincidir con la BD
                  const tipoLabels = {
                    SOLINGGSGA: "INGRESO SGA",
                    SOLEGRGSGA: "EGRESO SGA",
                    SOLEGRLOC: "TRANSF. LOCALIDAD",
                    SOLEGRPRES: "PRÉSTAMO",
                    SOLEGRBOD: "TRANSF. BODEGA",
                    SOLINGAJU: "AJUSTE INGRESO",
                    SOLEGRAJU: "AJUSTE EGRESO",
                  }
                  const label = tipoLabels[String(valor).toUpperCase()] || valor
                  return <span>{label}</span>
                },
              },
              {
                accessorKey: "tmotdescripcion",
                header: "Motivo",
                size: 200,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "integradescri",
                header: "Integrador",
                size: 180,
                Cell: ({ cell }) => <span>{cell.getValue() || "N/A"}</span>,
              },
              {
                accessorKey: "proyectodescri",
                header: "Proyecto",
                size: 180,
                Cell: ({ cell }) => <span>{cell.getValue() || "N/A"}</span>,
              },
              {
                accessorKey: "sgastatus",
                header: "Estado",
                size: 120,
                Cell: ({ cell }) => {
                  const valor = cell.getValue()
                  let color = "black"

                  if (valor === "PENDIENTE") color = "#f39c12" // Naranja
                  if (valor === "APROBADA") color = "#27ae60" // Verde
                  // Nuevos estados para ciclos asíncronos (Préstamos/Transferencias Locales)
                  if (valor === "EN PRESTAMO" || valor === "ENT.PAR." || valor === "EN TRANSITO") color = "#2980b9" // Azul
                  // Estados de cierre total
                  if (valor === "NEGADA" || valor === "CERRADA" || valor === "ENT.TOT.") color = "#c0392b" // Rojo

                  return <span style={{ fontWeight: "bold", color }}>{valor}</span>
                },
              },
              {
                accessorKey: "sgafecllegadaenvio",
                header: "Fecha Llegada/Envío",
                size: 150,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "sgafecsol",
                header: "Fecha Creación",
                size: 150,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "sgaususol",
                header: "Creado Por",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearSolicitudIngEgrIndex
